import AppLayout from "@/components/AppLayout";
import PageTitle from "@/components/PageTitle";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/Card";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/Table";
import { Separator } from "@/components/ui/Separator";

const SURFACE_TOKENS = [
  { name: "background", cls: "bg-background", text: "text-foreground" },
  { name: "card", cls: "bg-card", text: "text-card-foreground" },
  { name: "popover", cls: "bg-popover", text: "text-popover-foreground" },
  { name: "muted", cls: "bg-muted", text: "text-muted-foreground" },
];

const ACTION_TOKENS = [
  { name: "primary", cls: "bg-primary", text: "text-primary-foreground" },
  { name: "secondary", cls: "bg-secondary", text: "text-secondary-foreground" },
  { name: "accent", cls: "bg-accent", text: "text-accent-foreground" },
  { name: "destructive", cls: "bg-destructive", text: "text-destructive-foreground" },
];

const LINE_TOKENS = [
  { name: "border", cls: "border-border" },
  { name: "input", cls: "border-input" },
  { name: "ring", cls: "ring-ring" },
];

const STATUS_TOKENS = [
  { name: "success", surface: "bg-success-surface", text: "text-success" },
  { name: "warning", surface: "bg-warning-surface", text: "text-warning" },
  { name: "info", surface: "bg-info-surface", text: "text-info" },
];

const CHART_TOKENS = [
  { name: "chart-grid", cls: "bg-chart-grid" },
  { name: "chart-axis", cls: "bg-chart-axis", text: "text-white" },
  { name: "chart-muted", cls: "bg-chart-muted", text: "text-white" },
  { name: "chart-surface", cls: "bg-chart-surface" },
];

const RADIUS = [
  { name: "sm", cls: "rounded-sm" },
  { name: "md (default)", cls: "rounded-md" },
  { name: "lg", cls: "rounded-lg" },
  { name: "full", cls: "rounded-full" },
];

const SHADOWS = [
  { name: "sm", cls: "shadow-sm" },
  { name: "md", cls: "shadow-md" },
  { name: "lg", cls: "shadow-lg" },
];

const FONT_SIZES = [
  { name: "text-xs", cls: "text-xs", sample: "The quick brown fox — 12px metadata" },
  { name: "text-sm", cls: "text-sm", sample: "The quick brown fox — 14px dense body" },
  { name: "text-base", cls: "text-base", sample: "The quick brown fox — 16px body" },
  { name: "text-lg", cls: "text-lg", sample: "Subsection heading" },
  { name: "text-xl", cls: "text-xl", sample: "Section title" },
  { name: "text-2xl", cls: "text-2xl", sample: "Page title" },
];

const WEIGHTS = [
  { name: "font-normal", cls: "font-normal" },
  { name: "font-medium", cls: "font-medium" },
  { name: "font-semibold", cls: "font-semibold" },
];

const BUTTON_VARIANTS = ["default", "secondary", "outline", "ghost", "link", "destructive"] as const;
const BUTTON_SIZES = ["sm", "default", "lg", "icon"] as const;
const BADGE_VARIANTS = ["default", "secondary", "outline", "destructive", "success", "warning", "info"] as const;

function Section({ id, title, description, children }: { id: string; title: string; description?: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-6">
      <div className="mb-4">
        <h2 className="text-xl font-semibold">{title}</h2>
        {description && <p className="text-sm text-muted-foreground mt-1">{description}</p>}
      </div>
      {children}
    </section>
  );
}

function Swatch({ name, cls, text, extra }: { name: string; cls: string; text?: string; extra?: string }) {
  return (
    <div className="flex flex-col gap-2">
      <div className={`h-20 rounded-md border ${cls} ${text ?? ""} flex items-center justify-center text-xs font-medium`}>
        {text ? "Aa" : ""}
      </div>
      <div className="text-xs">
        <div className="font-medium">{name}</div>
        <div className="text-muted-foreground">{extra ?? cls}</div>
      </div>
    </div>
  );
}

function LineSwatch({ name, cls }: { name: string; cls: string }) {
  const isRing = cls.startsWith("ring-");
  return (
    <div className="flex flex-col gap-2">
      <div
        className={
          isRing
            ? `h-20 rounded-md bg-background ring-2 ring-offset-2 ring-offset-background ${cls} flex items-center justify-center text-xs`
            : `h-20 rounded-md bg-background border-2 ${cls} flex items-center justify-center text-xs`
        }
      >
        {name}
      </div>
      <div className="text-xs">
        <div className="font-medium">{name}</div>
        <div className="text-muted-foreground">{cls}</div>
      </div>
    </div>
  );
}

export default function DesignSystemIndex() {
  return (
    <AppLayout
      header={
        <PageTitle
          title="Design System"
          subtitle="Live reference for tokens and primitives"
        />
      }
    >
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-10">
        <header className="space-y-2">
          <h1 className="text-2xl font-semibold">Atlas Design System</h1>
          <p className="text-sm text-muted-foreground">
            Live reference for tokens and primitives. Values render directly from <code className="text-xs">application.css</code>,
            so what you see here is what ships.
          </p>
          <nav className="flex flex-wrap gap-x-4 gap-y-1 text-sm pt-2">
            <a href="#colors" className="text-link underline-offset-4 hover:underline">Colors</a>
            <a href="#radius" className="text-link underline-offset-4 hover:underline">Radius</a>
            <a href="#shadows" className="text-link underline-offset-4 hover:underline">Shadows</a>
            <a href="#typography" className="text-link underline-offset-4 hover:underline">Typography</a>
            <a href="#buttons" className="text-link underline-offset-4 hover:underline">Buttons</a>
            <a href="#badges" className="text-link underline-offset-4 hover:underline">Badges</a>
            <a href="#cards" className="text-link underline-offset-4 hover:underline">Cards</a>
            <a href="#tables" className="text-link underline-offset-4 hover:underline">Tables</a>
            <a href="#rules" className="text-link underline-offset-4 hover:underline">Rules</a>
          </nav>
        </header>

        <Separator />

        <Section id="colors" title="Colors" description="Semantic tokens only. Never reference raw palette classes (bg-purple-500) or hex literals in app code.">
          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Surfaces / text</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {SURFACE_TOKENS.map(t => <Swatch key={t.name} {...t} />)}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Actions</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {ACTION_TOKENS.map(t => <Swatch key={t.name} {...t} />)}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Lines</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {LINE_TOKENS.map(t => <LineSwatch key={t.name} {...t} />)}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Status (surface + text)</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {STATUS_TOKENS.map(t => (
                  <div key={t.name} className="flex flex-col gap-2">
                    <div className={`h-20 rounded-md border ${t.surface} ${t.text} flex items-center justify-center text-sm font-semibold`}>
                      {t.name}
                    </div>
                    <div className="text-xs">
                      <div className="font-medium">{t.name}</div>
                      <div className="text-muted-foreground">{t.surface} + {t.text}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Chart</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {CHART_TOKENS.map(t => (
                  <div key={t.name} className="flex flex-col gap-2">
                    <div className={`h-20 rounded-md border ${t.cls} ${t.text ?? ""} flex items-center justify-center text-xs font-medium`}>
                      {t.text && "Aa"}
                    </div>
                    <div className="text-xs">
                      <div className="font-medium">{t.name}</div>
                      <div className="text-muted-foreground">{t.cls}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>

        <Separator />

        <Section id="radius" title="Radius" description="Pick from the four below. `rounded` base, `rounded-xl`, `rounded-2xl` are not approved.">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {RADIUS.map(r => (
              <div key={r.name} className="flex flex-col gap-2">
                <div className={`h-20 bg-muted border ${r.cls}`} />
                <div className="text-xs">
                  <div className="font-medium">{r.name}</div>
                  <div className="text-muted-foreground">{r.cls}</div>
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Separator />

        <Section id="shadows" title="Shadows" description="Three levels only. No xl or 2xl.">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6 bg-muted rounded-lg">
            {SHADOWS.map(s => (
              <div key={s.name} className="flex flex-col gap-2">
                <div className={`h-20 bg-card rounded-md ${s.cls}`} />
                <div className="text-xs">
                  <div className="font-medium">{s.name}</div>
                  <div className="text-muted-foreground">{s.cls}</div>
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Separator />

        <Section id="typography" title="Typography">
          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Sizes</h3>
              <div className="space-y-3">
                {FONT_SIZES.map(f => (
                  <div key={f.name} className="flex items-baseline gap-6 border-b border-border pb-3">
                    <span className="text-xs text-muted-foreground w-24 shrink-0">{f.name}</span>
                    <span className={f.cls}>{f.sample}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Weights (no font-bold)</h3>
              <div className="space-y-3">
                {WEIGHTS.map(w => (
                  <div key={w.name} className="flex items-baseline gap-6 border-b border-border pb-3">
                    <span className="text-xs text-muted-foreground w-32 shrink-0">{w.name}</span>
                    <span className={`text-base ${w.cls}`}>The quick brown fox jumps over the lazy dog</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>

        <Separator />

        <Section id="buttons" title="Buttons" description="Always use <Button>. Do not roll your own <button> styling in pages.">
          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Variants</h3>
              <div className="flex flex-wrap gap-3 p-4 bg-muted rounded-lg">
                {BUTTON_VARIANTS.map(v => (
                  <Button key={v} variant={v}>{v}</Button>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Sizes</h3>
              <div className="flex flex-wrap items-center gap-3 p-4 bg-muted rounded-lg">
                {BUTTON_SIZES.map(s => (
                  <Button key={s} size={s}>{s === "icon" ? "★" : s}</Button>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Disabled</h3>
              <div className="flex flex-wrap gap-3 p-4 bg-muted rounded-lg">
                <Button disabled>default</Button>
                <Button variant="secondary" disabled>secondary</Button>
                <Button variant="outline" disabled>outline</Button>
                <Button variant="destructive" disabled>destructive</Button>
              </div>
            </div>
          </div>
        </Section>

        <Separator />

        <Section id="badges" title="Badges" description="Use <Badge> for status pills. Success/warning/info currently use raw palette — pending token extraction.">
          <div className="flex flex-wrap gap-3 p-4 bg-muted rounded-lg">
            {BADGE_VARIANTS.map(v => (
              <Badge key={v} variant={v}>{v}</Badge>
            ))}
          </div>
        </Section>

        <Separator />

        <Section id="cards" title="Cards">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Card title</CardTitle>
                <CardDescription>Short description that sits under the title.</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-foreground">
                  Card body content. Uses bg-card / text-card-foreground tokens, rounded-lg, and a subtle shadow-xs.
                </p>
              </CardContent>
              <CardFooter>
                <Button size="sm">Primary</Button>
                <Button size="sm" variant="ghost" className="ml-2">Cancel</Button>
              </CardFooter>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Metric card</CardTitle>
                <CardDescription>Inventory value</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-semibold">5,310,415,514</div>
                <div className="text-xs text-muted-foreground mt-1">across 5 warehouses, 73 SKUs</div>
              </CardContent>
            </Card>
          </div>
        </Section>

        <Separator />

        <Section id="tables" title="Tables">
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>SKU</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead className="text-right">In stock</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium">LX6A8C0F0609</TableCell>
                  <TableCell>Halio IPL Cooling Hair Removal</TableCell>
                  <TableCell className="text-right">193</TableCell>
                  <TableCell><Badge variant="warning">Not ordered</Badge></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">LXD80164117D</TableCell>
                  <TableCell>Halio Red Light Therapy 3-in-1</TableCell>
                  <TableCell className="text-right">164</TableCell>
                  <TableCell><Badge variant="success">Ordered</Badge></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Card>
        </Section>

        <Separator />

        <Section id="rules" title="Hard rules" description="Enforced in review and (soon) CI. See DESIGN_SYSTEM.md §7.">
          <ol className="list-decimal list-inside space-y-2 text-sm">
            <li>No raw hex literals in pages / components / layouts.</li>
            <li>No raw palette classes (<code className="text-xs">bg-purple-500</code>, <code className="text-xs">text-slate-700</code>) outside <code className="text-xs">components/ui/</code> and <code className="text-xs">wireframes/</code>.</li>
            <li>No inline <code className="text-xs">&lt;button&gt;</code> in pages — use <code className="text-xs">&lt;Button&gt;</code>.</li>
            <li>No arbitrary spacing brackets (<code className="text-xs">p-[13px]</code>) outside fixed layout widths.</li>
            <li>No <code className="text-xs">font-bold</code>, no bare <code className="text-xs">rounded</code>, no <code className="text-xs">rounded-xl</code>/<code className="text-xs">2xl</code>, no <code className="text-xs">shadow-xl</code>.</li>
          </ol>
        </Section>
      </div>
    </AppLayout>
  );
}
